import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import {
  AskScenario,
  clientApi,
  ScenarioDetail,
  ScenarioPreviewResp,
  ScenarioSuggestion,
} from '@/api/client'
import { orgScopedKey } from '@/auth/session'
import { qk } from '@/api/queryKeys'

/* ---------------------------------------------------------------------------
 * Phase G: saved scenarios are now first-class server-side entities.
 *
 * We still read the legacy ``saved_scenarios_v1`` localStorage key exactly
 * once so existing users don't lose their library; a small migration pass
 * POSTs each to ``/v1/scenarios`` and then removes the local key. After
 * that, the server is the source of truth for saved scenarios.
 * -------------------------------------------------------------------------*/

const LEGACY_KEY = 'saved_scenarios_v1'
const MIGRATION_DONE_KEY = 'saved_scenarios_v1_migrated'

type LegacySaved = {
  id: string
  label: string
  assumptions: string[]
  created_at: string
}

function readLegacySaved(): LegacySaved[] {
  try {
    const raw = localStorage.getItem(orgScopedKey(LEGACY_KEY))
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? (parsed as LegacySaved[]) : []
  } catch {
    return []
  }
}

function clearLegacy(): void {
  try {
    localStorage.removeItem(orgScopedKey(LEGACY_KEY))
    localStorage.setItem(orgScopedKey(MIGRATION_DONE_KEY), '1')
  } catch {
    /* non-fatal */
  }
}

function wasMigrated(): boolean {
  try {
    return localStorage.getItem(orgScopedKey(MIGRATION_DONE_KEY)) === '1'
  } catch {
    return false
  }
}

/* ---------------------------------------------------------------------------
 * Props
 * -------------------------------------------------------------------------*/

interface ScenarioEditorProps {
  label: string
  onLabelChange: (value: string) => void
  assumptions: string[]
  onAssumptionsChange: (values: string[]) => void
  /** Filled in when the editor is pinned to a specific server-side scenario
   *  (via "Load" or similarity-match confirmation). `null` means ad-hoc. */
  scenarioId?: string | null
  onScenarioIdChange?: (id: string | null) => void
  targetQuestion?: string | null
  disabled?: boolean
}

/* ---------------------------------------------------------------------------
 * ScenarioEditor — structured replacement for the old free-form textarea.
 * -------------------------------------------------------------------------*/

export default function ScenarioEditor({
  label,
  onLabelChange,
  assumptions,
  onAssumptionsChange,
  scenarioId,
  onScenarioIdChange,
  targetQuestion,
  disabled,
}: ScenarioEditorProps) {
  const queryClient = useQueryClient()
  const [preview, setPreview] = useState<ScenarioPreviewResp | null>(null)
  const [previewing, setPreviewing] = useState(false)
  const [previewError, setPreviewError] = useState<string | null>(null)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [saveStatus, setSaveStatus] = useState<string | null>(null)
  const [pendingSuggestions, setPendingSuggestions] = useState<ScenarioSuggestion[] | null>(null)

  const cleanedAssumptions = useMemo(
    () => assumptions.map((a) => a.trim()).filter(Boolean),
    [assumptions],
  )
  const hasScenario = cleanedAssumptions.length > 0

  /* --- Server-backed library --- */
  const savedQuery = useQuery({
    queryKey: qk.scenarios.list(),
    queryFn: () => clientApi.listScenarios(),
    staleTime: 30_000,
  })
  const saved: ScenarioDetail[] = savedQuery.data?.scenarios || []

  // One-time legacy migration: if the user has entries in localStorage and
  // we haven't migrated yet, POST them all to the server and drop the key.
  const migrationDoneRef = useRef(false)
  useEffect(() => {
    if (migrationDoneRef.current) return
    if (wasMigrated()) {
      migrationDoneRef.current = true
      return
    }
    const legacy = readLegacySaved()
    if (legacy.length === 0) {
      clearLegacy()
      migrationDoneRef.current = true
      return
    }
    migrationDoneRef.current = true
    ;(async () => {
      // `force_new: false` makes the server reuse an existing scenario when
      // (label, assumption texts) already match exactly. Critical here —
      // the migration effect can fire more than once in practice (org id
      // loads asynchronously so the orgScopedKey for MIGRATION_DONE_KEY
      // changes between first and second render), and we previously used
      // force_new=true which silently duplicated every saved scenario.
      for (const entry of legacy) {
        try {
          await clientApi.createScenarioEntity({
            label: entry.label,
            assumptions: entry.assumptions.map((t) => ({ text: t })),
            force_new: false,
          })
        } catch {
          /* best-effort — skip failing entries */
        }
      }
      clearLegacy()
      queryClient.invalidateQueries({ queryKey: qk.scenarios.all() })
    })()
  }, [queryClient])

  /* --- Row editing ------------------------------------------------------ */

  const addAssumption = () => onAssumptionsChange([...assumptions, ''])
  const updateAssumption = (idx: number, value: string) => {
    // Editing any field decouples the draft from its server-side origin
    // until the user explicitly re-saves or re-loads.
    if (onScenarioIdChange && scenarioId) onScenarioIdChange(null)
    onAssumptionsChange(assumptions.map((a, i) => (i === idx ? value : a)))
  }
  const removeAssumption = (idx: number) => {
    if (onScenarioIdChange && scenarioId) onScenarioIdChange(null)
    onAssumptionsChange(assumptions.filter((_, i) => i !== idx))
  }

  const handleLabelChange = (value: string) => {
    if (onScenarioIdChange && scenarioId) onScenarioIdChange(null)
    onLabelChange(value)
  }

  /* --- Save / load ------------------------------------------------------ */

  const canSave = hasScenario && label.trim().length > 0

  const saveMut = useMutation({
    mutationFn: (payload: { force_new: boolean }) =>
      clientApi.createScenarioEntity({
        label: label.trim(),
        assumptions: cleanedAssumptions.map((t) => ({ text: t })),
        force_new: payload.force_new,
      }),
    onSuccess: (res, variables) => {
      queryClient.invalidateQueries({ queryKey: qk.scenarios.all() })
      setSaveError(null)
      setPendingSuggestions(null)
      onScenarioIdChange?.(res.scenario.scenario_id)
      if (variables.force_new) {
        setSaveStatus('Saved as a new scenario.')
      } else if (res.reused) {
        setSaveStatus(`Matched an existing scenario (${res.scenario.label}) — reusing its history.`)
      } else {
        setSaveStatus(`Saved — scenario id ${res.scenario.scenario_id}.`)
      }
    },
    onError: (e: any) => setSaveError(e?.message || 'Save failed'),
  })

  const searchMut = useMutation({
    mutationFn: () =>
      clientApi.searchScenarios({
        label: label.trim(),
        assumptions: cleanedAssumptions.map((t) => ({ text: t })),
      }),
    onSuccess: (res) => {
      // If the server thinks this looks like an existing scenario, show
      // suggestions so the user can pick "use existing" vs "create new".
      // Honors the user's explicit preference: NEVER auto-merge.
      if (res.suggestions && res.suggestions.length > 0) {
        setPendingSuggestions(res.suggestions)
        setSaveError(null)
        setSaveStatus(null)
      } else {
        saveMut.mutate({ force_new: false })
      }
    },
    onError: (e: any) => setSaveError(e?.message || 'Similarity check failed'),
  })

  const saveCurrent = () => {
    if (!canSave) return
    setSaveError(null)
    setSaveStatus(null)
    searchMut.mutate()
  }

  const loadSaved = (entry: ScenarioDetail) => {
    onLabelChange(entry.label)
    onAssumptionsChange(
      entry.assumptions && entry.assumptions.length > 0
        ? entry.assumptions.map((a) => a.text)
        : [''],
    )
    onScenarioIdChange?.(entry.scenario_id)
    setPreview(null)
    setSaveStatus(`Loaded "${entry.label}" — this ask will reuse scenario ${entry.scenario_id}.`)
  }

  const loadSuggestion = (s: ScenarioSuggestion) => {
    loadSaved(s)
    setPendingSuggestions(null)
  }

  const archiveMut = useMutation({
    mutationFn: (id: string) => clientApi.archiveScenario(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.scenarios.all() })
    },
  })

  /* --- Preview ---------------------------------------------------------- */

  const runPreview = useCallback(async () => {
    if (!hasScenario) return
    setPreviewing(true)
    setPreviewError(null)
    try {
      const scenario: AskScenario = {
        assumption_mode: 'hard',
        assumptions: cleanedAssumptions.map((text, i) => ({ id: `a${i + 1}`, text })),
        ...(label.trim() ? { label: label.trim() } : {}),
      }
      const resp = await clientApi.previewScenario(scenario, targetQuestion || undefined)
      setPreview(resp)
    } catch (ex: any) {
      setPreviewError(ex?.message || 'Failed to preview scenario')
    } finally {
      setPreviewing(false)
    }
  }, [cleanedAssumptions, hasScenario, label, targetQuestion])

  const assumptionsKey = assumptions.map((a) => a.trim()).join('\x00')
  useEffect(() => {
    setPreview(null)
  }, [assumptionsKey, label])

  /* --- Render ----------------------------------------------------------- */

  return (
    <div className="ask-extra-card">
      <div className="ask-extra-label">
        Conditional scenario
        {scenarioId && (
          <span className="pill ok" style={{ marginLeft: 8, fontSize: 10 }}>
            saved
          </span>
        )}
        <span className="ask-extra-hint">
          {' '}
          — applies to this ask only. Use <em>Save</em> to reuse across future asks (now tracked server-side).
        </span>
      </div>

      <input
        className="input ask-input"
        type="text"
        placeholder="Scenario label (optional — e.g. “Fed cuts 50bps by July”)"
        value={label}
        onChange={(e) => handleLabelChange(e.target.value)}
        disabled={disabled}
      />

      <div style={{ display: 'grid', gap: 6, marginTop: 8 }}>
        {assumptions.length === 0 && (
          <div className="muted" style={{ fontSize: 12 }}>
            No assumptions yet. Add the world-state you want the forecast to
            treat as true.
          </div>
        )}
        {assumptions.map((value, idx) => (
          <div key={idx} className="row" style={{ gap: 6, alignItems: 'flex-start' }}>
            <span className="muted" aria-hidden="true" style={{ fontSize: 11, marginTop: 8, minWidth: 18 }}>
              {idx + 1}.
            </span>
            <input
              className="input"
              type="text"
              placeholder="e.g. Fed cuts rates by 50 bps before July 2026"
              value={value}
              onChange={(e) => updateAssumption(idx, e.target.value)}
              disabled={disabled}
              style={{ flex: 1 }}
              aria-label={`Assumption ${idx + 1}`}
            />
            <button
              className="btn small"
              type="button"
              onClick={() => removeAssumption(idx)}
              disabled={disabled}
              aria-label={`Remove assumption ${idx + 1}`}
              title="Remove this assumption"
            >
              Remove
            </button>
          </div>
        ))}
      </div>

      <div className="row" style={{ gap: 8, marginTop: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <button
          className="btn small"
          type="button"
          onClick={addAssumption}
          disabled={disabled || assumptions.length >= 20}
          title={assumptions.length >= 20 ? 'Maximum 20 assumptions per scenario' : 'Add an assumption'}
        >
          + Add assumption
        </button>
        <button
          className="btn small"
          type="button"
          onClick={runPreview}
          disabled={disabled || previewing || !hasScenario}
          title={hasScenario ? 'Show how the server will interpret these assumptions' : 'Add at least one assumption first'}
        >
          {previewing ? 'Previewing…' : 'Preview'}
        </button>
        <button
          className="btn small"
          type="button"
          onClick={saveCurrent}
          disabled={disabled || !canSave || saveMut.isPending || searchMut.isPending}
          title={canSave ? 'Save this scenario for reuse' : 'Add a label and at least one assumption to save'}
        >
          {saveMut.isPending || searchMut.isPending ? 'Saving…' : 'Save for reuse'}
        </button>
        {hasScenario && (
          <span className="muted" style={{ fontSize: 12 }}>
            {cleanedAssumptions.length} assumption{cleanedAssumptions.length === 1 ? '' : 's'}
          </span>
        )}
      </div>

      {saveError && (
        <div role="alert" className="muted" style={{ fontSize: 12, marginTop: 8, color: 'var(--bad)' }}>
          {saveError}
        </div>
      )}
      {saveStatus && !saveError && (
        <div className="muted" style={{ fontSize: 12, marginTop: 8, color: 'var(--ok)' }}>
          {saveStatus}
        </div>
      )}

      {pendingSuggestions && pendingSuggestions.length > 0 && (
        <div
          style={{
            marginTop: 10,
            padding: '10px 12px',
            borderRadius: 10,
            background: 'rgba(250, 204, 21, 0.08)',
            border: '1px solid rgba(250, 204, 21, 0.22)',
          }}
          role="group"
          aria-label="Similar scenarios found"
        >
          <div style={{ fontSize: 12, fontWeight: 800, marginBottom: 6 }}>
            Similar scenarios already exist — pick one or create new?
          </div>
          <div className="muted" style={{ fontSize: 12, marginBottom: 8 }}>
            Using an existing scenario keeps all of its forecast history in one place. Creating a new one gives
            you a distinct variation (useful when the label is the same but the assumptions differ).
          </div>
          <div className="stack" style={{ gap: 6 }}>
            {pendingSuggestions.slice(0, 5).map((s) => (
              <div
                key={s.scenario_id}
                className="row"
                style={{
                  gap: 8,
                  alignItems: 'center',
                  padding: 8,
                  border: '1px solid var(--border)',
                  borderRadius: 8,
                  background: 'rgba(255,255,255,0.02)',
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: 13 }}>{s.label}</div>
                  <div className="muted" style={{ fontSize: 11, lineHeight: 1.4 }}>
                    match: {s.match_reason} · label {(s.label_similarity * 100).toFixed(0)}% · assumptions{' '}
                    {(s.assumption_jaccard * 100).toFixed(0)}%
                  </div>
                </div>
                <button
                  className="btn small"
                  type="button"
                  onClick={() => loadSuggestion(s)}
                  disabled={disabled}
                >
                  Use this one
                </button>
              </div>
            ))}
          </div>
          <div className="row" style={{ gap: 8, marginTop: 8, justifyContent: 'flex-end' }}>
            <button
              type="button"
              className="btn small"
              onClick={() => setPendingSuggestions(null)}
              disabled={disabled}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn small primary"
              onClick={() => saveMut.mutate({ force_new: true })}
              disabled={disabled || saveMut.isPending}
            >
              Create as new scenario
            </button>
          </div>
        </div>
      )}

      {saved.length > 0 && (
        <details style={{ marginTop: 12 }}>
          <summary className="muted" style={{ fontSize: 12, cursor: 'pointer', userSelect: 'none' }}>
            Saved scenarios ({saved.length})
          </summary>
          <div className="stack" style={{ gap: 6, marginTop: 8 }}>
            {saved.map((s) => (
              <div
                key={s.scenario_id}
                className="row"
                style={{
                  gap: 8,
                  alignItems: 'center',
                  padding: 8,
                  border: '1px solid var(--border)',
                  borderRadius: 8,
                  background: 'rgba(255,255,255,0.02)',
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: 13 }}>{s.label}</div>
                  <div className="muted" style={{ fontSize: 11, lineHeight: 1.4, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {(s.assumptions || []).map((a) => a.text).join(' · ').slice(0, 180)}
                  </div>
                </div>
                <button className="btn small" type="button" onClick={() => loadSaved(s)} disabled={disabled}>
                  Load
                </button>
                <button
                  className="btn small"
                  type="button"
                  onClick={() => archiveMut.mutate(s.scenario_id)}
                  disabled={disabled || archiveMut.isPending}
                  aria-label={`Archive saved scenario ${s.label}`}
                >
                  Archive
                </button>
              </div>
            ))}
          </div>
        </details>
      )}

      {previewError && (
        <div className="muted" role="alert" style={{ fontSize: 12, marginTop: 8, color: 'var(--bad)' }}>
          {previewError}
        </div>
      )}

      {preview && preview.conditioning_guidance && (
        <div
          style={{
            marginTop: 10,
            padding: '10px 12px',
            borderRadius: 10,
            background: 'rgba(99, 102, 241, 0.08)',
            border: '1px solid rgba(99, 102, 241, 0.18)',
          }}
          aria-live="polite"
        >
          <div style={{ fontSize: 11, fontWeight: 800, textTransform: 'uppercase', color: 'var(--accent)', marginBottom: 6 }}>
            How the server will apply this
          </div>
          {preview.conditioning_guidance.conditioning_summary && (
            <div style={{ fontSize: 13, lineHeight: 1.5 }}>
              {preview.conditioning_guidance.conditioning_summary}
            </div>
          )}
          {Array.isArray(preview.conditioning_guidance.removed_uncertainties) && preview.conditioning_guidance.removed_uncertainties.length > 0 && (
            <div style={{ marginTop: 10 }}>
              <div className="muted" style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', marginBottom: 4 }}>
                Uncertainties dropped
              </div>
              <ul style={{ margin: '0 0 0 18px', padding: 0, fontSize: 12, lineHeight: 1.55 }}>
                {preview.conditioning_guidance.removed_uncertainties.slice(0, 4).map((x, i) => (
                  <li key={i}>{x}</li>
                ))}
              </ul>
            </div>
          )}
          {Array.isArray(preview.conditioning_guidance.implication_prompts) && preview.conditioning_guidance.implication_prompts.length > 0 && (
            <div style={{ marginTop: 10 }}>
              <div className="muted" style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', marginBottom: 4 }}>
                Implications agents will work through
              </div>
              <ul style={{ margin: '0 0 0 18px', padding: 0, fontSize: 12, lineHeight: 1.55 }}>
                {preview.conditioning_guidance.implication_prompts.slice(0, 6).map((x, i) => (
                  <li key={i}>{x}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
